<template>
  <div class="mode-page">
    <header class="mode-page__header">
      <div>
        <h1>{{ $t('saleMode.title') }}</h1>
        <p>{{ $t('saleMode.subtitle') }}</p>
      </div>
      <div v-if="!loading" class="mode-page__status">
        <span>{{ $t('saleMode.current') }}</span>
        <strong>{{ currentLabel }}</strong>
      </div>
    </header>

    <div v-if="loading" class="mode-page__loading">
      <i class="mdi mdi-loading mdi-spin"></i>
      <span>{{ $t('saleMode.loading') }}</span>
    </div>

    <div v-else class="mode-page__grid">
      <button
        v-for="mode in displayModes"
        :key="mode.value"
        type="button"
        class="mode-card"
        :class="{ 'mode-card--active': saleMode === mode.value }"
        :disabled="saving"
        @click="selectMode(mode.value)"
      >
        <div class="mode-card__top">
          <div class="mode-card__icon" :class="`mode-card__icon--${mode.value}`">
            <i :class="['mdi', mode.icon]"></i>
          </div>
          <span
            class="mode-card__radio"
            :class="{ 'mode-card__radio--on': saleMode === mode.value }"
            aria-hidden="true"
          >
            <i v-if="saleMode === mode.value" class="mdi mdi-check"></i>
          </span>
        </div>

        <div class="mode-card__body">
          <div class="mode-card__title-row">
            <h3>{{ mode.label }}</h3>
            <em v-if="saleMode === mode.value">{{ $t('saleMode.active') }}</em>
          </div>
          <p>{{ mode.description }}</p>
          <ul class="mode-card__points">
            <li v-for="point in mode.points" :key="point">
              <i class="mdi mdi-check-circle"></i>
              <span>{{ point }}</span>
            </li>
          </ul>
        </div>
      </button>
    </div>

    <div class="mode-page__note">
      <div class="mode-page__note-icon">
        <i class="mdi mdi-information-outline"></i>
      </div>
      <div>
        <strong>{{ $t('saleMode.howItWorks') }}</strong>
        <p>{{ $t('saleMode.note') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { getCompanySettings, updateCompanySettings } from '@/api/companySettings'
import { notify, notifyError } from '@/utils/notify'

const MODE_META = {
  catalog: {
    icon: 'mdi-package-variant-closed',
  },
  quick: {
    icon: 'mdi-flash-outline',
  },
}

export default {
  name: 'SaleModeControl',
  data() {
    return {
      loading: true,
      saving: false,
      saleMode: 'catalog',
      modes: [],
    }
  },
  computed: {
    displayModes() {
      const list = Array.isArray(this.modes) && this.modes.length
        ? this.modes
        : [
            {
              value: 'catalog',
              label: this.$t('saleMode.catalog.label'),
              description: this.$t('saleMode.catalog.description'),
            },
            {
              value: 'quick',
              label: this.$t('saleMode.quick.label'),
              description: this.$t('saleMode.quick.description'),
            },
          ]

      return list.map((mode) => ({
        ...mode,
        label: this.$t(`saleMode.${mode.value}.label`),
        description: this.$t(`saleMode.${mode.value}.description`),
        icon: (MODE_META[mode.value] || MODE_META.catalog).icon,
        points: this.$t(`saleMode.${mode.value}.points`),
      }))
    },
    currentLabel() {
      const current = this.displayModes.find((mode) => mode.value === this.saleMode)
      return current ? current.label : this.$t('saleMode.catalog.label')
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const data = await getCompanySettings()
        this.saleMode = data.sale_mode || 'catalog'
        this.modes = Array.isArray(data.modes) ? data.modes : []
        localStorage.setItem('sale_mode', this.saleMode)
      } catch (error) {
        notifyError(error)
      } finally {
        this.loading = false
      }
    },
    async selectMode(mode) {
      if (mode === this.saleMode || this.saving) return
      this.saving = true
      try {
        const data = await updateCompanySettings({ sale_mode: mode })
        this.saleMode = data.sale_mode
        localStorage.setItem('sale_mode', this.saleMode)
        notify(data.message || this.$t('saleMode.updated'))
      } catch (error) {
        notifyError(error)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
$ink: #152033;
$muted: #6b7a90;
$accent: #0b4f8a;
$line: #d9e2ec;

.mode-page {
  padding: 28px 32px 40px;
  max-width: 920px;
  color: $ink;
}

.mode-page__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 750;
    letter-spacing: -0.02em;
  }

  p {
    margin: 6px 0 0;
    color: $muted;
    font-size: 14px;
  }
}

.mode-page__status {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  padding: 10px 14px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid $line;
  min-width: 140px;

  span {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $muted;
  }

  strong {
    font-size: 14px;
    font-weight: 750;
    color: $accent;
  }
}

.mode-page__loading {
  display: flex;
  align-items: center;
  gap: 10px;
  color: $muted;
  padding: 48px 0;

  .mdi {
    font-size: 22px;
  }
}

.mode-page__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.mode-card {
  display: flex;
  flex-direction: column;
  gap: 18px;
  text-align: left;
  padding: 20px;
  border: 1.5px solid $line;
  border-radius: 18px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.15s ease, background 0.18s ease;

  &:hover:not(:disabled):not(.mode-card--active) {
    transform: translateY(-1px);
    border-color: #b7c6d6;
    box-shadow: 0 10px 24px rgba(18, 32, 51, 0.06);
  }

  &:disabled {
    opacity: 0.72;
    cursor: wait;
  }

  &--active {
    border-color: $accent;
    background: linear-gradient(180deg, #f7fbff 0%, #fff 70%);
    box-shadow: 0 0 0 3px rgba(11, 79, 138, 0.1);
  }
}

.mode-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.mode-card__icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: #eef4fa;
  color: $accent;

  .mdi {
    font-size: 24px;
  }

  &--quick {
    background: #fff4e5;
    color: #c2410c;
  }
}

.mode-card__radio {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 1.5px solid #c5d2df;
  background: #fff;
  display: grid;
  place-items: center;
  color: #fff;
  transition: all 0.15s ease;

  .mdi {
    font-size: 14px;
    font-weight: 700;
  }

  &--on {
    border-color: $accent;
    background: $accent;
  }
}

.mode-card__body {
  h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 750;
    color: $ink;
  }

  p {
    margin: 8px 0 0;
    color: $muted;
    font-size: 13.5px;
    line-height: 1.5;
  }
}

.mode-card__title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  em {
    font-style: normal;
    padding: 3px 9px;
    border-radius: 999px;
    background: #e8f1f8;
    color: $accent;
    font-size: 11px;
    font-weight: 750;
    letter-spacing: 0.02em;
  }
}

.mode-card__points {
  list-style: none;
  margin: 16px 0 0;
  padding: 14px 0 0;
  border-top: 1px solid #e8eef5;
  display: flex;
  flex-direction: column;
  gap: 10px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: #3d4f63;
    font-size: 13px;
    line-height: 1.4;
  }

  .mdi {
    margin-top: 1px;
    color: $accent;
    font-size: 16px;
  }
}

.mode-card--active .mode-card__icon--quick {
  background: #ffedd5;
}

.mode-page__note {
  display: flex;
  gap: 14px;
  margin-top: 20px;
  padding: 16px 18px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid $line;
}

.mode-page__note-icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #eef4fa;
  color: $accent;

  .mdi {
    font-size: 20px;
  }
}

.mode-page__note strong {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 750;
}

.mode-page__note p {
  margin: 0;
  color: $muted;
  font-size: 13.5px;
  line-height: 1.5;
}

@media (max-width: 720px) {
  .mode-page {
    padding: 20px 16px 32px;
  }

  .mode-page__header {
    flex-direction: column;
  }

  .mode-page__status {
    align-items: flex-start;
    width: 100%;
  }

  .mode-page__grid {
    grid-template-columns: 1fr;
  }
}
</style>
